/**
 * @fileoverview Student & parent guide — the Hans Academy content shown to
 * student-portal users. The instructor guide (GuidePage) covers recording and
 * roster management, which students never do; this guide instead walks students
 * and parents through the portal: reading lessons, logging practice, recording
 * rehearsals, building a portfolio, and the privacy/consent that protects them.
 * Shares the searchable UI in `components/GuideView`.
 *
 * Same scope rule as the instructor guide: only what production actually
 * ships. Two things this content used to get wrong, both fixed here —
 *
 *   - **Practice ASSIGNMENTS are dark in production** (REACT_APP_ASSIGNMENTS_ENABLED
 *     is QA-only in .github/workflows/deploy-frontend.yml), so the checklist a
 *     student was told to expect does not render for them. What they really get
 *     is the practice list inside each lesson summary, which is what this now
 *     describes.
 *   - **A lesson appears only once the teacher SENDS the summary**, not when
 *     the lesson is recorded — the old "appear here automatically" made a
 *     teacher's unsent draft sound like a portal outage.
 *
 * There is no parent LOGIN: parent accounts are a launch switch that has never
 * been turned on (PARENT_ACCOUNTS_ENABLED in runtime_config), so a parent reads
 * summaries by email or holds their child's portal account. Don't document a
 * parent portal until that flips.
 *
 * Screenshots live in `public/academy/` and are captured from the real portal
 * by `scripts/capture-academy.mjs`. Re-run it after a UI change.
 */

import {
  Rocket,
  FileText,
  Mic,
  Trophy,
  ShieldCheck,
  Settings,
  HelpCircle,
} from "lucide-react";
import GuideView from "@/components/GuideView";
import { supportEmail } from "@/lib/companyBrand";

/** Shorthand for an image in `public/academy`. Mirrors GuidePage's helper. */
const shot = (file, alt, caption) => ({
  src: `/academy/${file}`,
  alt,
  caption,
  kind: file.endsWith(".gif") ? "gif" : "image",
});

/* ------------------------------------------------------------------ */
/*  Student & parent guide content                                     */
/* ------------------------------------------------------------------ */

const studentGuideCategories = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting Started",
    sections: [
      {
        id: "what-is-the-portal",
        title: "What is the Student Portal?",
        content:
          "The Student Portal is your own space in Hans. When your teacher finishes writing up a lesson and sends it, the summary lands here — what you covered, what to practice, and the ideas behind it. You can also log your practice, record yourself playing, book your next lesson, and share your progress.",
        media: shot(
          "student-dashboard.png",
          "The Hans student portal dashboard showing recent lessons, a practice streak, and a button to record a rehearsal",
          "Your dashboard"
        ),
      },
      {
        id: "signing-in",
        title: "Signing In",
        content:
          "Your teacher invites you by email. Open the invite and follow the link to set a password — after that, sign in any time at the student login page with your email and password.",
        steps: [
          "Find the invitation email from your teacher (check spam/junk if you don't see it)",
          "Tap the link to create your account and set a password",
          "Sign in with your email and password whenever you return",
          'Forgot your password? Use "Forgot password" on the login screen to reset it',
        ],
        note: "Only your teacher can invite you. If you didn't get an invite, ask them to send (or resend) it from your student profile. If you already have a portal account with another teacher, sign in with the password you already have — a second account isn't created.",
      },
      {
        id: "for-parents",
        title: "For Parents",
        content:
          "If your child takes lessons, every summary their teacher sends reaches you by email — for a child under 13 it goes to you alone, and never to the child. There is no separate parent login: where a portal account makes sense, a parent usually holds it and signs in with the child. You'll see exactly what the teacher sent, including the practice list for the week.",
      },
      {
        id: "navigation",
        title: "Getting Around",
        content: "The menu gives you quick access to everything in your portal:",
        items: [
          { label: "Dashboard", desc: "What to practice this week, your streak, and your next and latest lessons" },
          { label: "Lessons", desc: "Every lesson summary your teacher has sent you" },
          { label: "Rehearsals", desc: "Record yourself practicing, read the feedback, and see every practice log" },
          { label: "Progress", desc: "Your streak, time practiced and milestones, and a page to share" },
          { label: "Profile", desc: "Your account, reminders, appearance, and help" },
        ],
      },
      {
        id: "appearance",
        title: "Light or dark",
        content:
          "Profile → Appearance switches between a light and a dark portal. The choice is saved on the device you made it on, so your phone and your laptop can be set differently.",
      },
    ],
  },
  {
    id: "lessons",
    icon: FileText,
    title: "Your Lessons",
    sections: [
      {
        id: "reading-lessons",
        title: "Reading a Lesson Summary",
        content:
          "Each summary captures what you covered so nothing gets forgotten between sessions. Depending on the lesson you may see any of these:",
        items: [
          { label: "Summary", desc: "A short recap of what the lesson covered" },
          { label: "Your focus", desc: "What you're working toward, and anything you asked for" },
          { label: "Practice this week", desc: "Exactly what to work on before your next lesson" },
          { label: "Notes from your teacher", desc: "Anything they wrote for you in their own words" },
          { label: "Core concepts", desc: "The underlying skills behind the work" },
        ],
        media: shot(
          "student-lesson.png",
          "A lesson summary in the Hans student portal, with the overview, practice list and the teacher's notes",
          "A lesson summary"
        ),
      },
      {
        id: "when-lessons-appear",
        title: "When a Lesson Shows Up",
        content:
          "A lesson appears here once your teacher has reviewed the write-up and sent it — not the moment the lesson ends. They may edit it first, so a summary usually lands a little after the lesson rather than during it. If a recent lesson still isn't here after a day or two, ask your teacher; they may not have sent it yet.",
      },
      {
        id: "lesson-history",
        title: "Looking Back at Past Lessons",
        content:
          "All of your lessons stay in the Lessons list, newest first, and you can search it. Tap any one to reopen the full summary — a good way to remember a tip from a few weeks ago, or to see how far you've come. Any lesson can be saved as a PDF from the lesson page.",
      },
      {
        id: "practice-from-lessons",
        title: "Knowing What to Practise",
        content:
          "Your practice list comes straight from the most recent lesson summary. Start there, work through it, and bring your questions to the next session. If your teacher set a goal with you, it's what the week's practice is building towards.",
      },
    ],
  },
  {
    id: "rehearsals",
    icon: Mic,
    title: "Practice & Rehearsals",
    sections: [
      {
        id: "logging-practice",
        title: "Logging Practice Time",
        content:
          "Keeping track of practice helps you build a habit and shows your teacher the effort you're putting in. Log practice from the yellow button, or from Log beside each item under Practice this week: say what it was for, how long, and which day. Progress turns that into a streak. A rehearsal you record counts as a practice day too — you don't have to log it twice.",
      },
      {
        id: "recording-rehearsals",
        title: "Recording a Rehearsal",
        content:
          "You can record yourself playing and send it to your teacher for feedback — perfect for checking a tricky passage between lessons.",
        steps: [
          "Open the Rehearsals page, or choose Record instead in Log practice",
          "Tap record and play your piece",
          "Stop when you're done and let it upload",
          "Your teacher can listen and leave feedback right on the recording",
        ],
        note: "Recordings can be up to 15 minutes long. Find a quiet spot and keep your device nearby for the clearest sound.",
        media: shot(
          "student-rehearsals.png",
          "The rehearsals page in the Hans student portal, listing practice recordings with feedback",
          "Your rehearsals"
        ),
      },
      {
        id: "rehearsal-feedback",
        title: "Getting Feedback",
        content:
          "Once your teacher reviews a rehearsal, their notes appear next to that recording. You may also see AI-generated feedback pointing out things to listen for — it is there to help you practice, and your teacher's word always comes first.",
      },
      {
        id: "practice-reminders",
        title: "Practice Reminders",
        content:
          "If you'd like a nudge, set a reminder on your Profile: pick the days and the hour, and Hans will remind you to practice. Turn it off from the same place whenever you want.",
      },
    ],
  },
  {
    id: "portfolio",
    icon: Trophy,
    title: "Your Progress",
    sections: [
      {
        id: "portfolio-overview",
        title: "Progress and your portfolio",
        content:
          "Progress shows your streak, the last few weeks of practice, the time you've put in and the milestones you've earned. Share opens your portfolio: a page that tells the story of your musical journey — the instruments you play, the repertoire you've worked on, goals you've reached, and the interests that are yours. It's built from what you've actually done and is yours to personalize.",
        media: shot(
          "student-portfolio.png",
          "A student portfolio page in Hans showing instruments, repertoire and milestones",
          "Share your progress"
        ),
      },
      {
        id: "portfolio-editing",
        title: "Personalizing It",
        content: "You can tailor your portfolio so it really feels like you:",
        items: [
          { label: "Interests", desc: "Add the styles, composers, or songs you love" },
          { label: "Manual additions", desc: "Include accomplishments that aren't captured automatically" },
          { label: "Instrument filter", desc: "Focus the page on a specific instrument when you play more than one" },
        ],
      },
      {
        id: "portfolio-sharing",
        title: "Sharing Your Portfolio",
        content:
          "Your portfolio is private until you say otherwise. Turn on the public page from the share card and you get a link you can send to family, a teacher, or an audition panel — it shows your playing, not your account details. Turn it off and the link stops working for everyone, including people you already sent it to. You can also download the portfolio as a PDF.",
        note: "If you're under 18, talk to a parent before you publish the page. The link works for anyone who has it.",
      },
    ],
  },
  {
    id: "privacy",
    icon: ShieldCheck,
    title: "Privacy & Consent",
    sections: [
      {
        id: "what-you-see",
        title: "What You Can See",
        content:
          "You only ever see your own data — your lessons, your practice, and your portfolio. You can't see other students, no one else can see your information, and your teacher's private notes about the lesson are not shown to you. Your teacher manages your account access.",
      },
      {
        id: "consent",
        title: "Consent for Recording",
        content:
          "Before a teacher can record lessons, consent must be given — by a parent or guardian for anyone under 18, or by the student for adults. You'll receive an email with the full details and can grant, decline, or revoke consent at any time using the links in that email. Every summary email carries the same withdrawal link.",
      },
      {
        id: "audio-handling",
        title: "How Recordings Are Handled",
        content:
          "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days. Lesson content is not used to train AI models. Everything is encrypted, and nothing is sold or used for advertising. If consent is withdrawn, recording stops and any audio still held is removed.",
      },
    ],
  },
  {
    id: "account",
    icon: Settings,
    title: "Account & Settings",
    sections: [
      {
        id: "profile-settings",
        title: "Your Profile",
        content:
          "The Profile page holds your details, your musical interests, the teachers you're connected to, your reminders and appearance, and quick links to help and privacy resources.",
      },
      {
        id: "notifications",
        title: "Notifications",
        content:
          "Turn notifications on or off from your Profile so you're told about new lesson summaries and feedback in the way that works best for you.",
      },
      {
        id: "password-reset",
        title: "Resetting Your Password",
        content:
          'If you forget your password, use "Forgot password" on the student login screen. You\'ll get an email with a secure link to set a new one.',
      },
    ],
  },
  {
    id: "help",
    icon: HelpCircle,
    title: "Help & FAQ",
    sections: [
      {
        id: "faq-no-invite",
        title: "I didn't get an invite email",
        content:
          "Check your spam or junk folder first. If it's still not there, the email address on file may be wrong — ask your teacher to confirm it and resend the invitation from your student profile.",
      },
      {
        id: "faq-no-lessons",
        title: "My lessons aren't showing up",
        content:
          "A lesson only appears once your teacher has sent the summary, and they often edit it first. If a recent lesson is still missing after a day or two, let your teacher know — it may be sitting in their drafts.",
      },
      {
        id: "faq-recording-mic",
        title: "My rehearsal recording can't access the microphone",
        content:
          "Make sure you've allowed microphone access. In your browser, look for a microphone icon in the address bar and set it to \"Allow\". On iOS, check Settings > Safari > Microphone.",
      },
      {
        id: "faq-app",
        title: "Is there an app?",
        content:
          "Yes — Hans has an iOS app, and the portal works in any browser on a phone, tablet or computer. You sign in the same way on all of them.",
      },
      {
        id: "faq-contact",
        title: "How do I get help?",
        content:
          `Ask your teacher first — they can help with most things about your lessons and account. For anything technical, you can also reach our team at ${supportEmail()}.`,
      },
    ],
  },
];

const StudentGuidePage = () => (
  <GuideView
    categories={studentGuideCategories}
    title="Hans Academy"
    intro="Your guide to the student portal — reading lessons, logging practice, and tracking your progress. Search below or browse by topic."
  />
);

export default StudentGuidePage;
